// Imports ------------------------------------------------------------------->
import {Component, attr, handler} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";

import {Dialog} from "../flipp-dialog/Dialog";
import {Autocomplete} from "./Autocomplete";

import {inject} from "../../services/di";
import {UserAgentService} from "../../services/UserAgentService";
import {LocationService} from "../../services/LocationService";
import {CategoryService} from "../../services/CategoryService";
import {LinkService} from "../../services/LinkService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {AuthenticationService} from "../../services/AuthenticationService";
import {AuthStatus} from "../../services/definitions/IAuthenticationService";

import {AutocompleteContext} from "../../definitions/CommonDefinitions";
import {ConfigService} from "../../services/ConfigService";

import {isCrawler} from "../../utils/crawler";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

interface SearchBoxProps extends JSX.HTMLProps {
	search: string;
	autocompletecontext: AutocompleteContext;
	handleAdd?: (item: string, canonicalCat: string) => void;
}

@element("flipp-searchbox")
export class SearchBox extends Component<SearchBoxProps, {}> {
	@attr search: string;
	@attr autocompletecontext: AutocompleteContext;
	@inject categoryService: CategoryService;
	@inject location: LocationService;
	@inject userAgent: UserAgentService;
	@inject analytics: AnalyticsService;
	@inject auth: AuthenticationService;
	@inject config: ConfigService;
	@handler handleAdd?: (item: string, canonicalCat: string) => void;

	state = {
		currentInput: "",
		loading: false,
		/* This value used to track whether or not the input value was changed. This
			* is needed in the case where the user uses on a deep search link and a
			* default value is provided */
		inputChanged: false
	};

	refs: {
		searchForm: HTMLFormElement,

		/* Maintain a reference to the 'Autocomplete' component which is what
			* displays the results. The autocomplete results have
			* been split out into a separate component to allow us to enforce that the
			* query input box maintains focus. This is a requirement on mobile as the
			* keyboard will disappear if the focus is lost.
			*
			* All updates to the autocomplete results are achieved by calling
			* invalidate() on the 'autocomplete' component. The 'SearchBox' component
			* (this one) is never actually re-rendered, which allows us to keep the
			* results Dialog component open (to prevent flashing) and maintain focus
			* on the input box (to keep the keyboard on mobile)
			*
			* FIXME: This approach causes problems with accessibility. By maintaining
			* focus on the input box, screen readers (such as Chromevox) are able to
			* 'break out' of the dialog content and read elements on the page.
			*/
		autocomplete: Autocomplete

		/* There are three input boxes which are used to listen for input. They
			* are positioned on the page as such:
			* [shiftTabInputTrap] [query] [tabInputTrap]
			*
			* [query] is the actual search box that is rendered on the page
			* [shiftTabInputTrap] and [tabInputTrap] are rendered on the page with width 0
			*
			* Rationale: We need to be able to control the tab indexing while the
			* autocomplete dialog is opened and ensure focus is only captured by
			* the links in the dialog. If the [query] input has focus and:
			*   - the user presses tab, [tabInputTrap] recieves focus
			*   - the user presses shift-tab, [shiftTabInputTrap] recieves focus
			* We can then adjust the focused item in the autocomplete dialog and then
			* reset the page focus back to [query]
			*/
		query: HTMLInputElement;
		shiftTabInputTrap: HTMLInputElement,
		tabInputTrap: HTMLInputElement,

		dialog: Dialog
	};

	onAttach() {
		window.addEventListener("flipp-auth", this._handleAuthEvent);
	}

	onDetach() {
		window.removeEventListener("flipp-auth", this._handleAuthEvent);
	}

	onRender() {
		if (this.refs.query) {
			/* Refocus the input box on render! (if not on mobile as it will sometimes bring up the device keyboard) */
			if (!bowser.mobile) {
				this.refs.query.focus();
			}

			if (this.autocompletecontext === AutocompleteContext.SL) {
				// For SL search, we need to clear out the query value.
				// For Global Search, we need to preserve the query value as we
				// redirect to the results page.
				// ------------------------------------------------------------------
				this.refs.query.value = this.state.currentInput || "";
			}
		}

		this.checkOpenDialog();
	}

	render() {
		const searchValue = this.search || this.state.currentInput || "";
		const isGlobalSearch = (this.autocompletecontext === AutocompleteContext.GLOBAL_SEARCH);
		const formClass = isGlobalSearch ? "search-box" : "item-form";

		const placeholder = isGlobalSearch
			? this.userAgent.mobile ? i18n("search_placeholder_mobile") : i18n("search_placeholder")
			: i18n("enter_item");

		const closeButton = (
			<button type="button" class="search-close">
					{icons.searchClose}
				</button>
		);

		const searchButton = (
			<button aria-label={i18n("search")} title={i18n("search")} class="search" type="submit" style={{backgroundColor: this.config.getThemeColor()}}>
					{icons.search}
			</button>
		);

		const addButton = (!this.state.loading ?
			<button class="add-icon" type="submit">
				<div class="hidden-plus" flex-horizontal>{icons.plus}</div>
			</button>
		:
			<div class="add-icon adding" flex-horizontal>
				<div class="spinner"></div>
			</div>
		);

		const decorPlus = (<span class="decor-plus">+</span>);

		return <form class={formClass}
								ref="searchForm"
								onSubmit={this.handleSubmit_}
								flex-horizontal flex-grow>
			<input type="hidden" name="postal_code" value={this.location.postalCode}/>
			<input type="hidden" name="locale" value={this.location.locale}/>
			<div flex-horizontal
					class="search-box-background">
				<div flex-grow>

					<input
						ref="shiftTabInputTrap"
						type="text"
						name="shift-tab"
						value="-1"
						class="tab-catcher"
						onFocus={this.handleTabFocus_}
						disabled/>
					<input ref="query"
								type="search" maxlength="1024" name="q"
								value={searchValue}
								placeholder={placeholder}
								title={placeholder}
								onInput={this.handleInput_}
								onKeyDown={this.handleKeyDown_}
								autocomplete="off"
								anchor-point="left bottom"
								aria-autocomplete="both" flex-grow />
					{!isGlobalSearch ? decorPlus : null}
					<input
						ref="tabInputTrap"
						type="text"
						name="tab"
						value="1"
						class="tab-catcher"
						onFocus={this.handleTabFocus_}
						disabled/>
				</div>
				{isGlobalSearch ? closeButton : null}
				{isGlobalSearch ? searchButton : addButton}
				{isCrawler() ? <a href="/search">Search</a> : null }
			</div>
			<Dialog classList={{
									"autocomplete-pop": true,
									"sl-search": !isGlobalSearch,
									"logged-in": this.auth.status === AuthStatus.LOGGED_IN
								}}
							ref="dialog"
							anchor-point="left top"
							onClose={this.handleDialogClose_}>
				<Autocomplete
					ref="autocomplete"
					value={searchValue}
					context={this.autocompletecontext}
					handleItemPicked={this.handleItemClicked_.bind(this)}
				/>
		</Dialog>
		</form>;
	}

	private checkOpenDialog() {
		if (this.state.inputChanged) {
			if (this.refs.autocomplete.hasResults && !this.refs.dialog.open) {
				this.refs.dialog.showModal(this.refs.query);
			} else if (!this.refs.autocomplete.hasResults && this.refs.dialog.open) {
				this.refs.dialog.close();
			}
		}
	}

	private handleSubmit_ = (e: Event) => {
		if (e) {
			e.preventDefault();
		}

		if (!this.refs.query.value) {
			this.refs.query.focus();
			return;
		}

		let idx;
		if (this.refs.autocomplete.focusIndex >= 0) {
			// If a user hit enter (submitted) a query while one of the autocomplete
			// suggestions was focused, then we need to make sure that we set the
			// index of the autocomplete suggestion, so that we can record it
			// as part of the autocomplete beacon (rank).
			// Otherwise, leave 'idx' parameter as "UNDEFINED".
			// --------------------------------------------------------------------
			idx = this.refs.autocomplete.focusIndex;
		}

		this.handleTransition_(this.refs.query.value, idx);
	}

	private handleTransition_ = (searchValue: string, idx?: number) => {
		const isGlobalSearch = this.autocompletecontext === AutocompleteContext.GLOBAL_SEARCH;

		if (this.refs.dialog.open) {
			this.refs.dialog.close();
		}

		// Fire autocomplete beacon.
		if (typeof idx !== "undefined") {
			// If idx field is defined, then that means that the autocomplete suggestion was clicked.
			// We want to track this via an autocomplete beacon. NOTE: The actual rank starts at 1, NOT 0.
			const context = isGlobalSearch ? "search" : "shopping_list";
			const rank = idx + 1;

			this.analytics.trackAutocompleteHit(this.refs.query.value, searchValue, rank, context);
		}

		if (this.state.loading) {
			return;
		}

		// Reset the input state so that the autocomplete dialog does not reopen on its own.
		// See onRender() --> checkOpenDialog.
		this.state.inputChanged = false;
		this.refs.query.value = searchValue;
		this.refs.query.setSelectionRange(searchValue.length, searchValue.length);

		if (isGlobalSearch) {
			const linkService = LinkService.getInstance();
			linkService.execute(linkService.linkTo("search", {
				q: searchValue
			}), this);
		} else {
			this.state.loading = true;
			this.invalidate().then(() => {
				this.categoryService.getCategoryForItems([searchValue.trim()]).then((categories) => {
					const canonicalCat = Array.isArray(categories) && categories[0] ? categories[0].canonical_category : "unknown";
					this.state.loading = false;
					this.state.currentInput = "";
					this.handleAdd(searchValue, canonicalCat);
					this.invalidate();
				});
			});
		}
	}

	private _handleAuthEvent = () => {
		if (this.autocompletecontext === AutocompleteContext.SL) {
			// This is needed in order to make sure that 'logged-in' class is set/unset.
			// This class controls the width of the Autocomplete modal, which is different
			// depending on whether the user is logged in or not. This is because when
			// user is logged in, there is an extra 'sharing' button present.
			// ----------------------------------------------------------------------
			this.invalidate();
		}
	}

	private handleDialogClose_ = () => {
		/* If the dialog is closed (eg: user clicks outside of dialg), then set
			* the input value back to only what was input - slice off the 'autocompleted'
			* portion */
		const selected = this.refs.autocomplete.selectedItem;
		if (selected) {
			this.refs.query.value = this.refs.query.value.slice(0, this.refs.query.selectionStart);
		}

		// Reset the input state so that the autocomplete dialog does not reopen on its own.
		// See onRender() --> checkOpenDialog.
		this.state.inputChanged = false;
		this.refs.query.focus();
		this.handleAutocompleteChanged_();
	}

	private handleItemClicked_ = (clickedItemName: string, idx: number, e: Event) => {
		if (e) {
			e.preventDefault();
		}
		this.handleTransition_(clickedItemName, idx);
	}

	private handleTabFocus_ = (e: FocusEvent) => {
		if (this.refs.dialog.open) {
			if (this.refs.query.selectionStart === this.refs.query.selectionEnd) {
				this.refs.query.setSelectionRange(this.refs.query.value.length, this.refs.query.value.length);
			}
			this.refs.query.value = this.refs.query.value.slice(0, this.refs.query.selectionStart);

			const input = e.target as HTMLInputElement;
			this.refs.autocomplete.adjustFocusedItem(Number(input.value));
			this.refs.autocomplete.invalidate().then(this.handleAutocompleteChanged_);
		}
		this.refs.query.focus();
	}

	private handleAutocompleteChanged_ = () => {
		/* We need to maintain focus() on the input boxes. Adjust the attributes
			* on the input fields to enable them to be focusable. This is needed
			* because showing the Dialog component will
			* setInert all elements on the page and they will not be focusable.
			*/
		const attrs = [
			{name: "tabindex", val: "-1"},
			{name: "data-flipp-tabindex", val: "0"},
			{name: "disabled", val: ""}
		];
		if (this.refs.dialog.open) {
			attrs.forEach((attr) => {
				this.refs.tabInputTrap.removeAttribute(attr.name);
				this.refs.shiftTabInputTrap.removeAttribute(attr.name);
			});
		} else {
			attrs.forEach((attr) => {
				this.refs.tabInputTrap.setAttribute(attr.name, attr.val);
				this.refs.shiftTabInputTrap.setAttribute(attr.name, attr.val);
			});
		}

		/* If there is a selected item, then call setSelectionRange */
		const selected = this.refs.autocomplete.selectedItem;
		if (selected && this.refs.dialog.open) {
			const currentInput = this.refs.query.value;
			this.refs.query.value = currentInput +
				selected.name.substr(currentInput.length, selected.name.length);
			this.refs.query.setSelectionRange(currentInput.length, selected.name.length);
			this.refs.query.setAttribute("aria-activedescendant", selected.itemId);
		} else if (this.refs.query.selectionStart > 0 && this.refs.query.selectionStart !== this.refs.query.selectionEnd) {
			this.refs.query.value = this.refs.query.value.slice(0, this.refs.query.selectionStart);
			this.refs.query.removeAttribute("aria-activedescendant");
		}
	}

	private handleInput_ = () => {
		if (this.refs.autocomplete.value.toLowerCase() === this.refs.query.value.toLowerCase()) {
			/* Don't rerender if the current value in the 'autocomplete' component
				* is the same as the current value in the input box. This can happen
				* in the cases where the input box contains an autocompleted entry (with
				* range selected) and pressing backspace/delete clears the selected
				* range. If we were to re-render again, the same autocomplete value would
				* appear and negate the backspace/delete action */
			return;
		}

		this.state.currentInput = this.refs.query.value;
		this.state.inputChanged = true;
		this.refs.autocomplete.value = this.refs.query.value;
		this.refs.autocomplete.invalidate().then(() => {
			this.checkOpenDialog();
			this.handleAutocompleteChanged_();
		});
	}

	private handleKeyDown_ = (e: KeyboardEvent) => {
		const isUpKey = e.keyCode === 38 || e.key === "ArrowUp";
		const isDownKey = e.keyCode === 40 || e.key === "ArrowDown";
		const isEscapeKey = e.keyCode === 27 || e.key === "Escape";

		if (isEscapeKey && this.refs.dialog.open) {
			e.preventDefault();
			this.refs.dialog.close();
		} else if (isUpKey || isDownKey) {
			/* Up/down keys move the cursor on input fields to the start/end by default
				* Call preventDefault here to avoid that behaviour
				*/
			e.preventDefault();

			/* Treat keyUp and keyDown as input, and clear the selection range
				* This is so we can identify what the user actually entered instead of
				* the full autocomplete value
				*/
			if (this.refs.query.selectionStart === this.refs.query.selectionEnd) {
				this.refs.query.setSelectionRange(this.refs.query.value.length, this.refs.query.value.length);
			}

			this.refs.query.value = this.refs.query.value.slice(0, this.refs.query.selectionStart);
			this.refs.autocomplete.adjustFocusedItem(isUpKey ? -1 : isDownKey ? 1 : 0);
			this.refs.autocomplete.invalidate().then(this.handleAutocompleteChanged_);
		}
	}
}
